// Admin status report — date-range view across all orders ingested in the
// window. Confirmed during UAT: filter basis is orders.created_at,
// on-screen summary + CSV download, three tabs (lifecycle / vendor / user).
//
// Defaults to the current month on first load so admin can just hit Run.

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminStatusReportApi, downloadAuthed } from '../api/services';
import LifecycleBadge from '../components/common/LifecycleBadge';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
  AlertTriangle, Users2, HandMetal, Truck, CheckCircle2,
  PackageCheck, PackageX, Hash, HelpCircle, Download, Loader2, Play, X,
  Info,
} from 'lucide-react';

// Filter option descriptions — surfaced via the (i) popover next to the
// Status dropdown so admins know exactly what each option pulls.
const STATUS_FILTER_HELP = [
  { value: 'all',               label: 'All (no filter)',     desc: 'Every order in the date range, regardless of status. Use this for a full export.' },
  { value: 'processed',         label: 'Processed',           desc: 'Orders past intake review that have moved into the dispatch pipeline. Includes Ready for dispatch, With vendor, In transit, and Closed by dispatch.' },
  { value: 'pending',           label: 'Pending',             desc: 'Orders still in pre-dispatch states: Intake, In review, Data incomplete, Needs attention. The to-do bucket for Dispatch / Ops.' },
  { value: 'in_transit',        label: 'In transit',          desc: 'Orders with tracking received from the vendor and the parcel currently moving (lifecycle = in_transit).' },
  { value: 'partially_shipped', label: 'Partially shipped',   desc: 'Multi-item orders where the vendor packed only some items and the rest were cancelled. OPS is arranging customer notification / per-item refund (lifecycle = partially_shipped).' },
  { value: 'delivered',         label: 'Closed by dispatch',  desc: 'Dispatch shared the tracking number with Ops and is done on its side (lifecycle = completed). Note: the parcel may still be "in transit" per India Post until physically delivered — see the Carrier statuses column for the live carrier view.' },
  { value: 'hold',              label: 'Hold',                desc: 'Orders paused — typically awaiting address verification or a customer hold request (lifecycle = on_hold).' },
  { value: 'reship',            label: 'Reship',              desc: 'Reship orders. Either the order is flagged is_reship=true, or it is still waiting to be linked to its original order (lifecycle = reship_unlinked).' },
  { value: 'cancelled',         label: 'Cancelled',           desc: 'Cancelled orders (lifecycle = cancelled). Refund handled separately, see Refund filter.' },
  { value: 'refund',            label: 'Refund',              desc: 'Delivered-but-unpaid orders awaiting refund or payment resolution (lifecycle = delivered_unpaid).' },
];

// Must stay in sync with client/src/components/intake/BatchStatusBreakdown.jsx
// BUCKETS order + tone. Same icons + palette for visual consistency.
const BUCKET_DISPLAY = {
  needs_attention: { label: 'Needs attention',        icon: AlertTriangle, tone: 'bg-red-50 border-red-200 text-red-800' },
  dispatch_team:   { label: 'With Dispatch team',     icon: Users2,        tone: 'bg-amber-50 border-amber-200 text-amber-800' },
  ops:             { label: 'With Ops',               icon: HandMetal,     tone: 'bg-blue-50 border-blue-200 text-blue-800' },
  vendor_transit:  { label: 'With vendor / In transit', icon: Truck,       tone: 'bg-indigo-50 border-indigo-200 text-indigo-800' },
  closed:          { label: 'Closed',                 icon: CheckCircle2,  tone: 'bg-gray-50 border-gray-200 text-gray-700' },
};

const TRACKING_DISPLAY = {
  tn_assigned:       { label: 'TN assigned',          icon: Hash,         tone: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
  tn_partial:        { label: 'TN partial',           icon: Hash,         tone: 'bg-amber-50 border-amber-200 text-amber-800' },
  tn_missing:        { label: 'TN missing',           icon: HelpCircle,   tone: 'bg-red-50 border-red-200 text-red-800' },
  carrier_live:      { label: 'Carrier tracking live', icon: Truck,       tone: 'bg-sky-50 border-sky-200 text-sky-800' },
  carrier_delivered: { label: 'Delivered',            icon: PackageCheck, tone: 'bg-green-50 border-green-200 text-green-800' },
  carrier_returned:  { label: 'Returned',             icon: PackageX,     tone: 'bg-rose-50 border-rose-200 text-rose-800' },
};

// Default range = current month, from 1st to today
function defaultRange() {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { from: fmt(first), to: fmt(today) };
}

export default function StatusReport() {
  const initial = defaultRange();
  const [from, setFrom] = useState(initial.from);
  const [to, setTo]     = useState(initial.to);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('lifecycle');
  // J1 (Kalpana UAT round 4): optional status filter on the CSV export.
  // Default 'all' = export everything (current behaviour).
  const [exportStatus, setExportStatus] = useState('all');
  const [statusHelpOpen, setStatusHelpOpen] = useState(false);
  // Drill-down state: { type, key, label } — set when user clicks a card/row.
  // null = collapsed.
  const [drilldown, setDrilldown] = useState(null);
  const [drillRows, setDrillRows] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);

  async function runReport() {
    setLoading(true);
    setDrilldown(null);
    try {
      const res = await adminStatusReportApi.summary(from, to);
      setSummary(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to build report');
    } finally {
      setLoading(false);
    }
  }

  // Whenever the drill-down selection changes (but the range is still current),
  // fetch the matching orders. Clicking the same card again clears the selection.
  useEffect(() => {
    if (!drilldown || !summary) return;
    let cancelled = false;
    setDrillLoading(true);
    adminStatusReportApi.orders(summary.from, summary.to, drilldown.type, drilldown.key)
      .then((res) => { if (!cancelled) setDrillRows(res.data.orders || []); })
      .catch((err) => {
        if (!cancelled) toast.error(err.response?.data?.error || 'Failed to load orders');
      })
      .finally(() => { if (!cancelled) setDrillLoading(false); });
    return () => { cancelled = true; };
  }, [drilldown, summary]);

  // Toggle helper — passing the same (type,key) twice clears the drill-down.
  function selectDrilldown(type, key, label) {
    if (drilldown && drilldown.type === type && drilldown.key === key) {
      setDrilldown(null);
      setDrillRows([]);
    } else {
      setDrilldown({ type, key, label });
    }
  }

  async function downloadCsv() {
    try {
      const statusQS = exportStatus && exportStatus !== 'all'
        ? `&status=${encodeURIComponent(exportStatus)}` : '';
      const suffix = exportStatus && exportStatus !== 'all' ? `-${exportStatus}` : '';
      await downloadAuthed(
        `/admin/status-report/export.csv?from=${from}&to=${to}${statusQS}`,
        `status-report-${from}_to_${to}${suffix}.csv`
      );
    } catch (err) {
      toast.error(err.response?.data?.error || 'Download failed');
    }
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Filter bar */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-semibold uppercase text-gray-500 tracking-wide mb-1">From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                 className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase text-gray-500 tracking-wide mb-1">To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                 className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </div>
        <button onClick={runReport} disabled={loading || !from || !to}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          Run report
        </button>
        {summary && (
          <>
            {/* J1 (UAT round 4): export filter — default 'all' is the
                current behaviour, anything else narrows by status. */}
            <label className="flex items-center gap-1 text-xs relative">
              <span className="font-semibold uppercase text-gray-500 tracking-wide">Status</span>
              <select value={exportStatus} onChange={(e) => setExportStatus(e.target.value)}
                      className="px-2 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="all">All (no filter)</option>
                <option value="processed">Processed</option>
                <option value="pending">Pending</option>
                <option value="in_transit">In transit</option>
                <option value="partially_shipped">Partially shipped</option>
                <option value="delivered">Closed by dispatch</option>
                <option value="hold">Hold</option>
                <option value="reship">Reship</option>
                <option value="cancelled">Cancelled</option>
                <option value="refund">Refund</option>
              </select>
              <button
                type="button"
                onClick={() => setStatusHelpOpen((v) => !v)}
                title="What does each status mean?"
                className="ml-1 p-1 text-gray-400 hover:text-blue-600 rounded-full hover:bg-blue-50"
                aria-label="Status filter help"
              >
                <Info className="w-4 h-4" />
              </button>
              {statusHelpOpen && (
                <div className="absolute left-0 top-full mt-1 z-20 w-[420px] bg-white border border-gray-300 rounded-lg shadow-lg p-3 text-xs text-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">Status filter options</span>
                    <button
                      type="button"
                      onClick={() => setStatusHelpOpen(false)}
                      className="text-gray-400 hover:text-gray-700"
                      aria-label="Close"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <ul className="space-y-2">
                    {STATUS_FILTER_HELP.map((o) => (
                      <li key={o.value}>
                        <div className="font-medium text-gray-900">{o.label}</div>
                        <div className="text-gray-600">{o.desc}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </label>
            <button onClick={downloadCsv}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
              <Download className="w-4 h-4" /> Download CSV
            </button>
          </>
        )}
        <div className="ml-auto text-xs text-gray-500">
          Filters by <span className="font-mono">orders.created_at</span>, inclusive.
        </div>
      </div>

      {!summary && !loading && (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-sm text-gray-500">
          Pick a date range and click <strong>Run report</strong>.
        </div>
      )}

      {summary && (
        <>
          {/* Top-line total */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
            <div className="text-xs uppercase font-semibold text-gray-500 tracking-wide">
              Orders raised {summary.from} to {summary.to}
            </div>
            <div className="text-3xl font-bold mt-1">{summary.total}</div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-4 flex gap-4">
            {[
              { key: 'lifecycle', label: 'By lifecycle' },
              { key: 'vendor',    label: 'By vendor' },
              { key: 'user',      label: 'By assigned user' },
            ].map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)}
                      className={`px-1 py-2 text-sm font-medium border-b-2 -mb-px ${
                        tab === t.key
                          ? 'border-blue-600 text-blue-600'
                          : 'border-transparent text-gray-600 hover:text-gray-900'
                      }`}>
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'lifecycle' && (
            <LifecycleTab summary={summary} drilldown={drilldown} onSelect={selectDrilldown} />
          )}
          {tab === 'vendor' && (
            <ByKeyTab rows={summary.byVendor} labelKey="vendor_name" idKey="vendor_id"
                      type="vendor" drilldown={drilldown} onSelect={selectDrilldown}
                      emptyText="No vendor assignments in this range." />
          )}
          {tab === 'user' && (
            <ByKeyTab rows={summary.byUser} labelKey="user_name" idKey="user_id"
                      type="user" drilldown={drilldown} onSelect={selectDrilldown}
                      emptyText="No assigned users in this range." />
          )}

          {/* Drill-down list — shows orders matching the selected card. */}
          {drilldown && (
            <DrillDownList
              drilldown={drilldown}
              rows={drillRows}
              loading={drillLoading}
              onClose={() => { setDrilldown(null); setDrillRows([]); }}
            />
          )}
        </>
      )}
    </div>
  );
}

function DrillDownList({ drilldown, rows, loading, onClose }) {
  return (
    <div className="mt-6 bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase font-semibold text-gray-500 tracking-wide">Orders in</div>
          <div className="font-semibold text-gray-900">{drilldown.label}</div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{loading ? '…' : `${rows.length} orders`}</span>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      {loading ? (
        <div className="p-8 text-center text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading orders…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-500">No orders match.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-3 py-2">Order</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Created</th>
                <th className="text-left px-3 py-2">Stage</th>
                <th className="text-left px-3 py-2">Vendor</th>
                <th className="text-left px-3 py-2">Owner</th>
                <th className="text-left px-3 py-2">Shipments</th>
                <th className="text-left px-3 py-2">Tracking</th>
                <th className="text-left px-3 py-2">Carrier</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((o) => (
                <tr key={o.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link to={`/orders/${o.id}`} className="text-blue-600 hover:underline">
                      {o.order_id_external}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{o.customer_name}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {o.created_at ? format(new Date(o.created_at), 'dd MMM yyyy') : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <LifecycleBadge stage={o.lifecycle_stage} ownerTeam={o.current_owner_team} />
                  </td>
                  <td className="px-3 py-2 text-xs">{o.vendor_name || '—'}</td>
                  <td className="px-3 py-2 text-xs">{o.owner_user || '—'}</td>
                  <td className="px-3 py-2 text-xs">
                    {o.shipments_with_tn || 0} / {o.shipment_count || 0}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-gray-600">
                    {o.tracking_numbers || '—'}
                  </td>
                  <td className="px-3 py-2 text-xs">{o.carrier_statuses || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// isActive(drilldown, type, key) — used to highlight the currently-open card.
function isActive(d, type, key) {
  return d && d.type === type && d.key === key;
}

function LifecycleTab({ summary, drilldown, onSelect }) {
  return (
    <>
      <h3 className="text-sm font-semibold text-gray-700 mb-2">Lifecycle buckets</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        {Object.entries(summary.byBucket).map(([key, n]) => {
          const d = BUCKET_DISPLAY[key] || { label: key, tone: '', icon: CheckCircle2 };
          const Icon = d.icon;
          const active = isActive(drilldown, 'bucket', key);
          return (
            <button key={key}
                    onClick={() => onSelect('bucket', key, d.label)}
                    disabled={n === 0}
                    className={`border rounded-lg p-3 text-left transition
                      ${d.tone}
                      ${active ? 'ring-2 ring-offset-1 ring-blue-500' : ''}
                      ${n === 0 ? 'opacity-40 cursor-not-allowed' : 'hover:shadow-sm cursor-pointer'}`}>
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide opacity-80">
                <Icon className="w-3.5 h-3.5" /> {d.label}
              </div>
              <div className="text-2xl font-bold mt-1">{n}</div>
            </button>
          );
        })}
      </div>

      <h3 className="text-sm font-semibold text-gray-700 mb-2">By exact stage</h3>
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 flex flex-wrap gap-2">
        {Object.entries(summary.byStage).length === 0
          ? <span className="text-sm text-gray-500">—</span>
          : Object.entries(summary.byStage).map(([stage, n]) => {
              const active = isActive(drilldown, 'stage', stage);
              return (
                <button key={stage}
                        onClick={() => onSelect('stage', stage, `Stage: ${stage}`)}
                        className={`inline-flex items-center gap-1 px-1 py-0.5 rounded transition
                          ${active ? 'ring-2 ring-offset-1 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'}`}>
                  <LifecycleBadge stage={stage} />
                  <span className="text-xs font-semibold opacity-70">×{n}</span>
                </button>
              );
            })}
      </div>

      <h3 className="text-sm font-semibold text-gray-700 mb-2">Tracking</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {Object.entries(summary.tracking).map(([key, n]) => {
          const d = TRACKING_DISPLAY[key] || { label: key, tone: '', icon: Hash };
          const Icon = d.icon;
          const active = isActive(drilldown, 'tracking', key);
          return (
            <button key={key}
                    onClick={() => onSelect('tracking', key, d.label)}
                    disabled={n === 0}
                    className={`border rounded-lg p-2 text-left transition
                      ${d.tone}
                      ${active ? 'ring-2 ring-offset-1 ring-blue-500' : ''}
                      ${n === 0 ? 'opacity-40 cursor-not-allowed' : 'hover:shadow-sm cursor-pointer'}`}>
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide opacity-80">
                <Icon className="w-3.5 h-3.5" /> {d.label}
              </div>
              <div className="text-xl font-bold mt-0.5">{n}</div>
            </button>
          );
        })}
      </div>
    </>
  );
}

function ByKeyTab({ rows, labelKey, idKey, type, drilldown, onSelect, emptyText }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-sm text-gray-500">
        {emptyText}
      </div>
    );
  }
  const total = rows.reduce((a, r) => a + r.count, 0);
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
          <tr>
            <th className="text-left px-4 py-2">{labelKey === 'vendor_name' ? 'Vendor' : 'User'}</th>
            <th className="text-right px-4 py-2">Orders</th>
            <th className="text-right px-4 py-2">Share</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r) => {
            const key = r[idKey] || '__none';
            const label = r[labelKey];
            const active = isActive(drilldown, type, key);
            return (
              <tr key={key}
                  onClick={() => onSelect(type, key, `${type === 'vendor' ? 'Vendor' : 'User'}: ${label}`)}
                  className={`cursor-pointer ${active ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                <td className="px-4 py-2">{label}</td>
                <td className="px-4 py-2 text-right font-semibold">{r.count}</td>
                <td className="px-4 py-2 text-right text-xs text-gray-500">
                  {total > 0 ? `${Math.round((r.count / total) * 100)}%` : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
